import { Images } from "../../../types/listingImages";
import { SelectedImages } from "./types";

// Natural real-estate tour flow: curb appeal / exterior, entry, main living,
// kitchen, specialty rooms, bedrooms, bathrooms, utility, feature details,
// outdoor living, neighborhood, then floor plan / unknown last. Values are the
// exact Vision `tag` strings assigned to listing images at upload time.
//
// MB-1478: an entry may be an ARRAY of strings that share one rank. Four tags
// used to be stored with the parenthetical gloss the api's prompt rendered
// inline ("Closets (walk-in, reach-in)"); the api now stores the BARE form
// ("Closets"), because that is what its downstream consumers have always
// matched on and the gloss moved out of the tag. Both spellings are listed
// together so the existing backlog keeps its place in the tour AND newly
// tagged photos rank too — dropping the glossed form would send every
// already-stored photo to the end of the tour, which is the same bug in the
// other direction. Pairs with api MB-1478.
const TAG_ORDER: readonly (string | readonly string[])[] = [
    "Front Exterior",
    "Aerial View",
    "Street View",
    "Driveway",
    "Garage Exterior",
    "Detached Garage",
    "Rear Exterior",
    // MB-1647: an outside photo the api cannot place as the front or the rear.
    "Exterior View",
    "Roof",
    "Entryway/Foyer",
    "Common Areas",
    "Living Room",
    "Family Room",
    "Dining Room",
    "Kitchen",
    "Home Office/Study",
    "Library",
    "Home Theater",
    "Game Room",
    "Gym/Workout Room",
    "Master Bedroom",
    "Bedrooms",
    "Bedroom",
    "Guest Bedroom",
    "Master Bathroom",
    "Bathrooms",
    "Guest Bathroom",
    "Half Bathroom/Powder Room",
    "Laundry Room",
    "Mudroom",
    ["Closets", "Closets (walk-in, reach-in)"],
    "Hallways",
    "Basement",
    "Attic",
    "Storage and Utility",
    "Storage Room",
    "Garage",
    "Fireplace",
    "Staircase",
    ["Built-ins", "Built-ins (shelves, cabinets)"],
    "Windows/Views",
    "Flooring",
    "Lighting Fixtures",
    "Historical/Architectural Details",
    "Sustainability Features",
    "Porch",
    "Patio",
    "Deck",
    "Balcony",
    "Garden/Landscaping",
    "Pool",
    "Hot Tub",
    "Sauna",
    ["Outdoor Features", "Outdoor Features (e.g., fountains, fire pits)"],
    ["Neighborhood Amenities", "Neighborhood Amenities (parks, playgrounds)"],
    "Floor Plan",
    "Unknown",
];

const RANK = new Map<string, number>(
    TAG_ORDER.flatMap((entry, i) =>
        (typeof entry === "string" ? [entry] : entry).map(
            (tag) => [tag, i] as const
        )
    )
);
// Untagged listing images and assets (which have no tag) sort to the end.
const UNRANKED = TAG_ORDER.length;

const tagRank = (tag?: string): number =>
    tag && RANK.has(tag) ? (RANK.get(tag) as number) : UNRANKED;

// Reorders the user's selected images into the natural tour sequence above,
// based on each image's Vision tag. Stable: images sharing a rank — and assets,
// which have no tag — keep their original selection order. Returns a new array;
// the input is not mutated.
export const orderImagesByTag = (
    selected: SelectedImages[],
    images: Images[]
): SelectedImages[] => {
    const tagFor = (sel: SelectedImages): string | undefined =>
        sel.type === "asset"
            ? undefined
            : images.find((img) => img.id === sel.id)?.tag;

    return selected
        .map((sel, index) => ({ sel, index, rank: tagRank(tagFor(sel)) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map((entry) => entry.sel);
};

// Same natural tour ordering for a plain list of listing images (used by the
// change-primary picker). Stable within a rank; the input is not mutated.
export const orderListingImagesByTag = (images: Images[]): Images[] =>
    images
        .map((image, index) => ({ image, index, rank: tagRank(image.tag) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map((entry) => entry.image);
